import { PROJECT_CATCH_ERROR_LIST } from "@platformx/utilities";
import graphqlInstance from "../../config/graphqlConfig";
import { PUBLISH_CONTENT } from "../../graphQL/mutations/contentTypeMutations";
import {
  SCHEDULE_ACTION_DOCUMENTS,
  ScheduleAction,
  ScheduleFamily,
} from "../../graphQL/mutations/contentScheduleMutations";

/**
 * Schedule API calls for content types (KIWI-6576). Unlike `contentTypeSchemaApi.publishContentType`,
 * which swallows errors and returns `{}`, everything here REJECTS on failure — a schedule that did
 * not take effect must never look like a success, and the backend's message ("must be in the
 * future", "already trigger is initiated") has to reach the editor.
 */
const send = async <T>(
  query: any,
  variables: any,
  isShared: boolean,
  label: string,
): Promise<T> => {
  try {
    const { data } = await graphqlInstance.query({
      query,
      variables,
      context: { skipBrandNameHeader: isShared },
      fetchPolicy: "no-cache",
    });
    return data as T;
  } catch (err: any) {
    console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:${label}`, err);
    throw err;
  }
};

export type ScheduleActionRequest = {
  family: ScheduleFamily;
  action: ScheduleAction;
  /** `{ contentType, input }` for fixed types; plus `schema_document_type` for schema types. */
  variables: Record<string, unknown>;
  isShared?: boolean;
};

const contentScheduleApi = {
  /**
   * Publish (or schedule a publish/unpublish of) a schema-based item. Rejects if the response
   * carries no `authoring_publishSchemaContent` payload, so a silent failure cannot pass as success.
   */
  publishSchemaContent: async <T = any>(variables: Record<string, unknown>, isShared = false) => {
    const data: any = await send<T>(PUBLISH_CONTENT, variables, isShared, "publishSchemaContent");
    if (!data?.authoring_publishSchemaContent) {
      throw new Error("publishSchemaContent returned no result");
    }
    return data as T;
  },

  /** Amend or cancel a pending schedule. */
  runAction: <T = any>({ family, action, variables, isShared = false }: ScheduleActionRequest) =>
    send<T>(SCHEDULE_ACTION_DOCUMENTS[family][action], variables, isShared, `schedule:${action}`),
};

export default contentScheduleApi;
